import { logError, logInformation, logWarning } from '@core/logging';
import { useEffect, useState } from 'react';
import { serializeError } from 'serialize-error';

export function useVideoStream() {
    const [videoDeviceId, setVideoDeviceId] = useState<string | null>(null);
    const [videoQuality, setVideoQuality] = useState<string | null>(null);
    const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
    const [videoFps, setVideoFps] = useState<number | null>(null);
    const [hasVideoError, setHasVideoError] = useState(false);

    useEffect(() => {
        if (!videoDeviceId || videoDeviceId === 'rtmp' || videoDeviceId === 'srt') {
            setVideoStream(null);
            setVideoQuality(null);
            setVideoFps(null);
            setHasVideoError(false);
            return;
        }

        let stream: MediaStream | null = null;
        const getVideoStream = async () => {
            const streamPriority = [
                {
                    description: '720p @ 30fps',
                    constraints: {
                        deviceId: { exact: videoDeviceId || '' },
                        width: { exact: 1280 },
                        height: { exact: 720 },
                        frameRate: { exact: 30 }
                    }
                },
                {
                    description: '720p @ any-fps',
                    constraints: {
                        deviceId: { exact: videoDeviceId || '' },
                        width: { exact: 1280 },
                        height: { exact: 720 },
                        frameRate: { ideal: 30 }
                    }
                },
                {
                    description: '720p or higher @ 30fps',
                    constraints: {
                        deviceId: { exact: videoDeviceId || '' },
                        width: { min: 1280 },
                        height: { min: 720 },
                        frameRate: { exact: 30 }
                    }
                },
                {
                    description: '720p or higher @ any-fps',
                    constraints: {
                        deviceId: { exact: videoDeviceId || '' },
                        width: { min: 1280 },
                        height: { min: 720 },
                        frameRate: { ideal: 30 }
                    }
                },
                {
                    description: 'Any @ any-fps',
                    constraints: {
                        deviceId: { exact: videoDeviceId || '' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 }
                    }
                }
            ];

            logInformation('Retrieving video stream for {VideoDevice}', { VideoDevice: videoDeviceId });
            for (const { description, constraints } of streamPriority) {
                try {
                    stream = await navigator.mediaDevices.getUserMedia({ video: constraints });

                    const settings = stream.getVideoTracks()[0].getSettings();
                    logInformation('Successfully obtained {Description} video stream {@Settings}', {
                        Settings: settings,
                        Description: description,
                        Constraints: constraints
                    });

                    if (settings.frameRate !== undefined && settings.frameRate > 0) {
                        setVideoFps(settings.frameRate);
                    } else {
                        // Default to 30fps
                        setVideoFps(30);
                        logWarning('Frame rate not specified, defaulting to 30fps');
                    }

                    if (settings.frameRate !== undefined && settings.width !== undefined && settings.height !== undefined) {
                        setVideoQuality(`${settings.width}x${settings.height} @ ${settings.frameRate}fps`);
                    } else {
                        setVideoQuality(description);
                    }
                    break;
                } catch (e) {
                    logWarning('Failed to get {Description} video stream ({Error})', {
                        Error: serializeError(e),
                        Description: description,
                        Constraints: constraints
                    });
                }
            }

            if (!stream) {
                logError('Failed to get video stream');
                setVideoQuality('Failed to get video stream');
            }

            setHasVideoError(!stream);
            setVideoStream(stream);
        };

        getVideoStream();
        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [videoDeviceId]);

    return { videoStream, hasVideoError, setVideoDeviceId, videoQuality, videoFps };
}
